import React, { useEffect, useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ChevronDown, X, Search } from 'lucide-react';

interface User {
  user_id: string;
  user_name: string;
}

interface ChatHistoryRequest {
  app_id: string;
  user_id: string;
  date_range: {
    start_date: string | null;
    end_date: string | null;
  };
  topic_search: string;
  pagination: {
    pageSize: number;
    skip: number;
    searchTerm: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

interface ChatHistoryFiltersProps {
  selectedDateRange: [Date | null, Date | null];
  setSelectedDateRange: React.Dispatch<React.SetStateAction<[Date | null, Date | null]>>;
  showDatePicker: boolean;
  setShowDatePicker: React.Dispatch<React.SetStateAction<boolean>>;
  datePickerRef: React.RefObject<HTMLDivElement>;
  selectedUser: string;
  setSelectedUser: React.Dispatch<React.SetStateAction<string>>;
  users: User[];
  topicSearchTerm: string;
  setTopicSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  selectedSortBy: string;
  setSelectedSortBy: React.Dispatch<React.SetStateAction<string>>;
  selectedSortOrder: 'asc' | 'desc';
  setSelectedSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  assistant: any;
  fetchHistory: (request: ChatHistoryRequest) => Promise<void>;
  pagination: {
    pageSize: number;
    skip: number;
    searchTerm: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

const ChatHistoryFilters: React.FC<ChatHistoryFiltersProps> = ({
  selectedDateRange,
  setSelectedDateRange,
  showDatePicker,
  setShowDatePicker,
  datePickerRef,
  selectedUser,
  setSelectedUser,
  users,
  topicSearchTerm,
  setTopicSearchTerm,
  selectedSortBy,
  setSelectedSortBy,
  selectedSortOrder,
  setSelectedSortOrder,
  assistant,
  fetchHistory,
  pagination,
}) => {
  // User autocomplete states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Topic search debounce
  const topicSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle click outside date picker and user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const [startDate, endDate] = selectedDateRange;

      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        startDate &&
        endDate
      ) {
        setShowDatePicker(false);
      }
      
      // Handle click outside user dropdown
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };
    
    if (showDatePicker || showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, selectedDateRange, datePickerRef, setShowDatePicker, showUserDropdown]);

  // Filter users based on search term
  useEffect(() => {
    if (!userSearchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.user_name.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [users, userSearchTerm]);

  // Update search term when selected user changes
  useEffect(() => {
    if (selectedUser) {
      const user = users.find(u => u.user_name === selectedUser);
      setUserSearchTerm(user ? user.user_name : '');
    } else {
      setUserSearchTerm('');
    }
  }, [selectedUser, users]);

  // Debounced topic search
  useEffect(() => {
    if (topicSearchTimeoutRef.current) {
      clearTimeout(topicSearchTimeoutRef.current);
    }

    topicSearchTimeoutRef.current = setTimeout(() => {
      // Topic search will trigger the useEffect in parent component
      // No need to call API directly here
    }, 300);

    return () => {
      if (topicSearchTimeoutRef.current) {
        clearTimeout(topicSearchTimeoutRef.current);
      }
    };
  }, [topicSearchTerm]);

  const handleDateRangeChange = (dates: [Date | null, Date | null] | null) => {
    if (!dates) {
      setSelectedDateRange([null, null]);
      return;
    }
  
    const [start, end] = dates;
    setSelectedDateRange([start, end]);
  
    if (start && end) {
      setShowDatePicker(false);
  
      const request: ChatHistoryRequest = {
        app_id: assistant.app_id,
        user_id: "",
        date_range: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        },
        topic_search: topicSearchTerm,
        pagination: {
          pageSize: 10,
          skip: 0,
          searchTerm: "",
          sortBy: selectedSortBy,
          sortOrder: selectedSortOrder,
        },
      };
  
      fetchHistory(request);
    }
  };

  const handleUserInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUserSearchTerm(value);
    setShowUserDropdown(true);
    
    // If input is cleared, reset selected user
    if (!value.trim()) {
      setSelectedUser('');
    }
  };

  const handleUserSelect = (userName: string) => {
    setSelectedUser(userName);
    setUserSearchTerm(userName);
    setShowUserDropdown(false);
  };

  const handleAllUsersSelect = () => {
    setSelectedUser('');
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const clearUserSelection = () => {
    setSelectedUser('');
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleUserInputFocus = () => {
    setShowUserDropdown(true);
  };

  const handleTopicSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTopicSearchTerm(event.target.value);
  };

  const clearTopicSearch = () => {
    setTopicSearchTerm('');
  };

  const handleSortByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSortBy(event.target.value);
  };

  const handleSortOrderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSortOrder(event.target.value as 'asc' | 'desc');
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  // Helper function to format the date range display
  const getDateRangeDisplay = () => {
    if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
      return `${selectedDateRange[0].toLocaleDateString()} - ${selectedDateRange[1].toLocaleDateString()}`;
    }
    return '';
  };

  return (
    <div className="mt-4 flex space-x-4 w-full px-4">
      {/* Date Range Picker */}
      <div className="flex flex-col items-start space-y-4 w-[20%]">
        <label className="text-sm">Filter by Date Range</label>
        <input
          type="text"
          value={getDateRangeDisplay()}
          readOnly
          placeholder="Select date range"
          className="border rounded-md p-2 cursor-pointer w-full"
          onClick={toggleDatePicker}
        />
        {showDatePicker && (
          <div ref={datePickerRef} className="absolute top-[30%] left-[6%] mt-2 z-10">
            <DatePicker
              selected={selectedDateRange[0]}
              onChange={handleDateRangeChange}
              startDate={selectedDateRange[0]}
              endDate={selectedDateRange[1]}
              selectsRange={true}
              isClearable={true}
              inline={true}
              className="border rounded-md p-2"
            />
          </div>
        )}
      </div>

      {/* Username Autocomplete */}
      {String(assistant?.is_authentication_enabled) === 'true' && (
        <div className="flex flex-col items-start space-y-4 w-[20%]" ref={userDropdownRef}>
          <label className="text-sm">Filter by User</label>
          <div className="relative w-full">
            <div className="relative">
              <input
                type="text"
                value={userSearchTerm}
                onChange={handleUserInputChange}
                onFocus={handleUserInputFocus}
                placeholder="Search users..."
                className="border rounded-md p-2 w-full pr-8"
              />
              {userSearchTerm && (
                <button
                  onClick={clearUserSelection}
                  className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" 
              />
            </div>
            
            {showUserDropdown && (
              <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-20">
                {/* All Users Option */}
                <div
                  onClick={handleAllUsersSelect}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 font-medium text-gray-700"
                >
                  All Users
                </div>
                
                {/* Filtered Users */}
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => handleUserSelect(user.user_name)}
                      className={`px-3 py-2 hover:bg-gray-100 cursor-pointer ${
                        selectedUser === user.user_name ? 'bg-blue-50 text-blue-700' : ''
                      }`}
                    >
                      {user.user_name}
                    </div>
                  ))
                ) : userSearchTerm.trim() ? (
                  <div className="px-3 py-2 text-gray-500 italic">
                    No users found matching "{userSearchTerm}"
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Topic Search */}
      <div className={`flex flex-col items-start space-y-4 ${String(assistant?.is_authentication_enabled) === 'true' ? 'w-[20%]' : 'w-[25%]'}`}>
        <label className="text-sm">Filter by Topic</label>
        <div className="relative w-full">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={topicSearchTerm}
              onChange={handleTopicSearchChange}
              placeholder="Search topics..."
              className="border rounded-md p-2 pl-8 pr-8 w-full"
            />
            {topicSearchTerm && (
              <button
                onClick={clearTopicSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sort By Dropdown */}
      <div className={`flex flex-col items-start space-y-4 ${String(assistant?.is_authentication_enabled) === 'true' ? 'w-[20%]' : 'w-[25%]'}`}>
        <label className="text-sm">Sort By</label>
        <select
          value={selectedSortBy}
          onChange={handleSortByChange}
          className="border rounded-md p-2 w-full"
        >
          <option value="last_updated">Date/Time</option>
          <option value="user_name">Name</option>
          <option value="title">Topic</option>
        </select>
      </div>

      {/* Sort Order Dropdown */}
      <div className={`flex flex-col items-start space-y-4 ${String(assistant?.is_authentication_enabled) === 'true' ? 'w-[20%]' : 'w-[25%]'}`}>
        <label className="text-sm">Order</label>
        <select
          value={selectedSortOrder}
          onChange={handleSortOrderChange}
          className="border rounded-md p-2 w-full"
        >
          <option value="desc">Newest First</option>
          <option value="asc">Oldest First</option>
        </select>
      </div>
    </div>
  );
};

export default ChatHistoryFilters;